<template>
  <div class="tw:flex tw:flex-col tw:flex-1 tw:bg-white tw:w-full tw:max-w-[160rem] tw:mx-auto" role="main">
    <div
      aria-label="registration form"
      class="tw:h-full tw:flex tw:items-center tw:justify-center tw:bg-white tw:p-4"
      role="region"
    >
      <div class="tw:sm:max-w-lg tw:md:max-w-md tw:mx-auto tw:flex tw:flex-col tw:w-full">
        <form class="tw:flex tw:flex-col tw:flex-1 tw:sm:px-0 tw:pt-16 tw:pb-32 tw:sm:pt-12 tw:md:pb-0">
          <div class="tw:mb-4 tw:sm:mb-6">
            <EdroneHeader
              type="h1"
              mobile-type="h2"
              :desc="translation.subtitle"
              :title="translation.title"
              :centered="true"
            />
          </div>

          <p v-if="googleError" class="tw:mb-2 tw:text-sm tw:text-red-500">
            {{ googleError }}
          </p>

          <GoogleSignInButton
            :translations="translation.google"
            class="tw:mb-4"
            context="register"
            @success="handleGoogleFillForm"
            @error="handleGoogleError"
          />

          <div class="tw:flex tw:items-center tw:gap-3 tw:mb-4">
            <div class="tw:flex-1 tw:h-px tw:bg-gray-300"></div>
            <span class="tw:text-gray-500 tw:text-sm">{{ translation.google?.or || "or" }}</span>
            <div class="tw:flex-1 tw:h-px tw:bg-gray-300"></div>
          </div>

          <div class="tw:space-y-3">
            <Field v-slot="{ value, handleChange, errorMessage, handleBlur }" name="name">
              <EdroneInput
                :error-msg="errorMessage"
                :invalid="!!errorMessage"
                :label="translation.name.label"
                :placeholder="translation.name.placeholder"
                :value="value"
                autocomplete="given-name"
                name="firstName"
                @blur="() => handleBlur()"
                @input="handleChange"
              />
            </Field>

            <Field v-slot="{ value, handleChange, errorMessage, handleBlur }" name="email">
              <EdroneInput
                :error-msg="formValidationErrorsBackend.email || errorMessage"
                :invalid="!!formValidationErrorsBackend.email || !!errorMessage"
                :label="translation.email.label"
                :placeholder="translation.email.placeholder"
                :value="value"
                autocomplete="email"
                name="email"
                @blur="
                  () => {
                    handleBlur();
                    setShopUrlByEmail(value);
                  }
                "
                @input="
                  (val) => {
                    handleChange(val);
                    clearBackendError('email');
                  }
                "
              />
            </Field>

            <Field v-slot="{ value, handleChange, errorMessage, handleBlur }" name="phoneNumber">
              <EdroneInput
                :error-msg="errorMessage"
                :invalid="!!errorMessage"
                :label="translation.phone.label"
                :placeholder="translation.phone.placeholder"
                :value="value"
                autocomplete="tel"
                name="phoneNumber"
                @blur="() => handleBlur()"
                @input="handleChange"
              />
            </Field>

            <Field v-slot="{ value, handleChange, errorMessage, handleBlur }" name="shopUrl">
              <EdroneInput
                :error-msg="formValidationErrorsBackend.shopUrl || errorMessage"
                :invalid="!!formValidationErrorsBackend.shopUrl || !!errorMessage"
                :label="translation.url.label"
                :placeholder="translation.url.placeholder"
                :value="value"
                autocomplete="url"
                name="shopUrl"
                @blur="
                  () => {
                    ensureHttpsPrefix(value);
                    handleBlur();
                  }
                "
                @input="
                  (val) => {
                    handleChange(val);
                    clearBackendError('shopUrl');
                  }
                "
              />
            </Field>

            <RegistrationCodeInput :initial-code="initialRegistrationCode" />
          </div>

          <div class="tw:hidden tw:md:flex tw:mt-10">
            <EdroneButton
              :disabled="!isFormValidForSubmit"
              :loading="isLoading"
              class="tw:w-full tw:h-full"
              unique-id="RegisterForm_submit-button"
              @click="handleFormSubmit"
            >
              {{ translation.submit }}
            </EdroneButton>
          </div>
          <p class="tw:hidden tw:md:block tw:text-center tw:text-xs tw:text-default-600 tw:mt-2">
            *{{ trans._register.platforms.noCardRequired }}
          </p>

          <p
            v-if="formValidationErrorsBackend.captcha"
            class="tw:hidden tw:md:flex tw:md:justify-center tw:caption tw:text-red-500 tw:mt-2"
          >
            {{ translation.notification.error.register }}
          </p>

          <p class="tw:body-2 tw:mt-6 tw:text-center" v-html="agreementsText"></p>
        </form>

        <div
          class="tw:fixed tw:bottom-0 tw:left-0 tw:right-0 tw:bg-white tw:border-t tw:border-default-200 tw:p-4 tw:z-20 tw:shadow-lg tw:md:hidden"
        >
          <div class="tw:max-w-xs tw:sm:max-w-xl tw:mx-auto tw:flex tw:flex-col tw:gap-2">
            <EdroneButton
              :disabled="!isFormValidForSubmit"
              :loading="isLoading"
              class="tw:w-full"
              unique-id="RegisterForm_submit-button-mobile"
              @click="handleFormSubmit"
            >
              {{ translation.submit }}
            </EdroneButton>
            <p class="tw:text-sm tw:text-center tw:mt-3">
              {{ trans._register.platforms.haveAccount }}
              {{ ' ' }}
              <a href="/login" class="tw:text-blue-500 tw:no-underline">
                {{ trans._register.platforms.login }}
              </a>
            </p>
          </div>
          <p v-if="formValidationErrorsBackend.captcha" class="tw:caption tw:text-red-500 tw:text-center tw:mt-2">
            {{ translation.notification.error.register }}
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import axios from "axios";
import { ref, onMounted, computed, nextTick } from "vue";
import { storeToRefs } from "pinia";
import { useRegisterStore } from "vue-app-new/modules/Register/store/Register.store";
import { splitAtFirstSpace } from "./RegisterForm.helpers";
import { amplitudeSendEvent } from "vue-app/utils/amplitude";
import { useRestrictedDomains } from "./composables/useRestrictedDomains/useRestrictedDomains.composable";
import { Field, useForm } from "vee-validate";
import { toTypedSchema } from "@vee-validate/zod";
import { RegisterFormSchema, RegisterFormDefaultValues, RegisterFormModel } from "./RegisterForm.model";
import EdroneButton from "vue-app/edroneComponents/EdroneButton.vue";
import EdroneInput from "vue-app/edroneComponents/EdroneInput.vue";
import EdroneHeader from "edrone-components/EdroneHeader.vue";
import RegistrationCodeInput from "./components/RegistrationCodeInput/RegistrationCodeInput.vue";
import { DTOTrackerProduct } from "vue-app/api/dto";
import { useCaptcha } from "vue-app/composables/useCaptcha";
import { type GoogleUserData } from "vue-app/composables/useGoogleSignIn/useGoogleSignIn.composable";
import GoogleSignInButton from "vue-app/components/Account/GoogleSignInButton/GoogleSignInButton.vue";

const { restrictedDomains } = useRestrictedDomains();

const translation = window._trans._register.form;
const urlParams = new URL(location.href).searchParams;

const store = useRegisterStore();
const { formValidationErrorsBackend, hasRegistrationCode, registrationCode } = storeToRefs(store);

const initialRegistrationCode = urlParams.get("code") || undefined;

const googleError = ref<string>("");
const googleUserData = ref<GoogleUserData | null>(null);

onMounted(() => {
  window.scrollTo(0, 0);
});

function handleGoogleError(errorMessage: string) {
  googleError.value = errorMessage;
}

function handleGoogleFillForm(userData: GoogleUserData) {
  googleError.value = "";

  const fullName = `${userData.firstName} ${userData.lastName}`.trim();
  setFieldValue("name", fullName);
  setFieldValue("email", userData.email);

  const emailDomain = userData.email.split("@")[1];
  if (emailDomain && emailDomain !== "gmail.com") {
    setFieldValue("shopUrl", `https://${emailDomain}`);
  }

  googleUserData.value = userData;

  nextTick(() => {
    const phoneInput = document.querySelector('input[name="phoneNumber"]') as HTMLInputElement;
    if (phoneInput) {
      phoneInput.focus();
    }
  });
}

const getInitialShopUrl = () => {
  const shopUrlParam = urlParams.get("shopUrl");
  if (shopUrlParam) {
    return shopUrlParam.startsWith("http://") || shopUrlParam.startsWith("https://")
      ? shopUrlParam
      : `https://${shopUrlParam}`;
  }
  return RegisterFormDefaultValues.shopUrl;
};

const { handleSubmit, values, meta, setFieldValue } = useForm<RegisterFormModel>({
  validationSchema: toTypedSchema(RegisterFormSchema),
  initialValues: {
    ...RegisterFormDefaultValues,
    shopUrl: getInitialShopUrl(),
  },
});

const isLoading = ref<boolean>(false);

const isFormValidForSubmit = computed(() => {
  return (
    meta.value.valid &&
    !formValidationErrorsBackend.value.email &&
    !formValidationErrorsBackend.value.shopUrl &&
    !formValidationErrorsBackend.value.captcha
  );
});

const setShopUrlByEmail = (emailValue: string) => {
  const userDomain = emailValue.split("@")[1];
  if (userDomain && !values.shopUrl && restrictedDomains.value && !restrictedDomains.value.includes(userDomain)) {
    setFieldValue("shopUrl", `https://${userDomain}`);
  }
};

const ensureHttpsPrefix = (urlValue: string) => {
  if (urlValue && !urlValue.startsWith("http://") && !urlValue.startsWith("https://")) {
    setFieldValue("shopUrl", `https://${urlValue}`);
  }
};

const clearBackendError = (field: "email" | "shopUrl") => {
  formValidationErrorsBackend.value[field] = "";
};

const agreementsText = translation.agreements.text
  .replace(
    /\{Terms of Service\}/g,
    `<a href="${translation.agreements.links.termsOfService}" target="_blank" class="tw:text-blue-500">${translation.agreements.termsOfService}</a>`
  )
  .replace(
    /\{Privacy Policy\}/g,
    `<a href="${translation.agreements.links.privacyPolicy}" target="_blank" class="tw:text-blue-500">${translation.agreements.privacyPolicy}</a>`
  );

const handleFormSubmit = handleSubmit(
  async () => {
    const { executeRecaptcha } = useCaptcha();
    const token = await executeRecaptcha("REGISTER");
    if (token) {
      await register(token);
    }
  },
  ({ errors }) => {
    amplitudeSendEvent("register_form_validation_failed", {
      step: "form",
      name_error: errors.name || "",
      email_error: errors.email || "",
      phone_error: errors.phoneNumber || "",
      url_error: errors.shopUrl || "",
      captcha_error: "",
    });
  }
);

async function register(token: string) {
  isLoading.value = true;

  amplitudeSendEvent("register_api_request_start", {
    step: "form",
    is_google_registration: !!googleUserData.value,
  });

  try {
    axios.get<DTOTrackerProduct[]>("/products/scrapped", {
      params: { url: values.shopUrl },
    });
    const { data } = await axios.post("/register", {
      first_name: splitAtFirstSpace(values.name)[0],
      last_name: splitAtFirstSpace(values.name)[1] || "",
      email: values.email,
      subscribe: true,
      phone: values.phoneNumber,
      url: values.shopUrl,
      registration_code: hasRegistrationCode.value ? registrationCode.value : null,
      onboarding_id: "",
      g_recaptcha_response: token,
      lang: urlParams.get("lang"),
      confirmed: urlParams.get("confirmed"),
      google_id: googleUserData.value?.googleId || null,
    });

    if (data.status === "success") {
      amplitudeSendEvent("register_success", {
        email_domain: values.email.split("@")[1],
        has_referral_code: !!urlParams.get("code"),
        language: urlParams.get("lang") || "en",
        is_google_registration: !!googleUserData.value,
      });
      window.location.href = "/login/trackers";
    } else {
      let errorType = "unknown";
      let errorMessage = "";

      if (data.error._email === "Email already exists") {
        errorType = "email_exists";
        errorMessage = translation.validation.backend.emailExists;
        formValidationErrorsBackend.value.email = translation.validation.backend.emailExists;
      }

      if (data.error._email === "Email is invalid") {
        errorType = "email_invalid";
        errorMessage = translation.validation.backend.emailIsInvalid;
        formValidationErrorsBackend.value.email = translation.validation.backend.emailIsInvalid;
      }

      if (data.error._captcha === "Captcha not valid") {
        errorType = "captcha_invalid";
        errorMessage = translation.validation.backend.captchaInvalid;
        formValidationErrorsBackend.value.captcha = translation.validation.backend.captchaInvalid;
      }

      amplitudeSendEvent("register_form_api_error", {
        error_type: errorType,
        error_message: errorMessage,
        step: "form",
      });
    }
  } catch (error) {
    amplitudeSendEvent("register_form_api_error", {
      error_type: "network_error",
      error_message: "Network error or server error",
      step: "form",
    });
  } finally {
    isLoading.value = false;
  }
}
</script>

<style>
@supports (-webkit-touch-callout: none) {
  @media screen and (max-device-width: 812px) and (orientation: portrait) {
    input,
    select,
    textarea {
      font-size: 16px !important;
    }
  }
}
</style>
